/**
 * Per-route SEO metadata — the single source of truth.
 *
 * Consumed twice, deliberately:
 *
 *  1. At build time by scripts/prerender-meta.mjs, which writes a real static
 *     HTML file per route with these tags baked into the <head>. This is the
 *     half that matters for crawlers which never execute JavaScript — GPTBot,
 *     ClaudeBot and PerplexityBot all fall in that group, so without it every
 *     route looks identical to an AI answer engine.
 *
 *  2. At runtime by useDocumentMeta, so client-side navigation updates the tab
 *     title and the tags Googlebot sees after it renders.
 *
 * Keep descriptions between roughly 150 and 160 characters, and titles under
 * 60, so Google renders them without truncating.
 */

import { GUIDES } from './guides';

export type RouteMeta = {
  /** Path as served, no trailing slash except the root. */
  path: string;
  title: string;
  description: string;
  /** Omit to fall back to the site-wide OG image. */
  ogImage?: string;
};

export const SITE_URL = 'https://www.defellix.com';

export const DEFAULT_META: RouteMeta = {
  path: '/',
  title: 'Legal Freelance Contracts for India | Defellix',
  description:
    'Create legally binding freelance contracts in minutes: OTP-signed, blockchain-timestamped, court-ready. Built for developers, designers and video editors.',
};

export const ROUTE_META: RouteMeta[] = [
  DEFAULT_META,
  {
    path: '/pricing',
    title: 'Freelance Contract Pricing from ₹0 | Defellix',
    description:
      'Start free with one contract, or buy a pack. Every plan includes OTP-signed agreements, blockchain timestamping and a tamper-evident evidence trail. No subscription required.',
  },
  {
    path: '/signup',
    title: 'Create a Freelance Contract Free | Defellix',
    description:
      'Sign up free and create your first freelance contract in minutes. Your client signs by OTP. No card needed. Built for Indian freelancers.',
  },
  {
    path: '/login',
    title: 'Log In | Defellix',
    description:
      'Log in to Defellix to send contracts, track milestones, record payments and generate court-ready evidence for your freelance work.',
  },
  {
    path: '/score-methodology',
    title: 'How the Credibility Score Works | Defellix',
    description:
      'A transparent breakdown of how Defellix calculates freelancer credibility: contracts completed, on-time delivery, client reviews and dispute history.',
  },
  {
    path: '/contact',
    title: 'Contact Defellix | Freelance Contract Support',
    description:
      'Questions about contracts, disputes or pricing? Get in touch with the Defellix team. We reply to every message from real freelancers.',
  },
  {
    path: '/terms',
    title: 'Terms of Service | Defellix',
    description:
      'The terms governing your use of Defellix: account rules, acceptable use, payment terms, and the limits of what a contract platform does and does not do.',
  },
  {
    path: '/privacy',
    title: 'Privacy Policy | Defellix',
    description:
      'What data Defellix collects, why, and how it is stored, written to India’s DPDP Act. Includes exactly what we receive when you sign in with Google.',
  },
];

ROUTE_META.push({
  path: '/guides',
  title: 'Freelance Contract Guides for India | Defellix',
  description:
    'Practical guides on freelance contracts in India: what to do when a client will not pay, which clauses matter, and whether e-signed agreements hold up in court.',
});

// Guides contribute their own entries, so adding one to GUIDES is all that is
// needed for its title, description and canonical to exist everywhere.
for (const g of GUIDES) {
  ROUTE_META.push({
    path: `/guides/${g.slug}`,
    title: g.title,
    description: g.metaDescription,
  });
}

export function metaForPath(pathname: string): RouteMeta {
  const clean = pathname !== '/' ? pathname.replace(/\/+$/, '') : '/';
  return ROUTE_META.find((m) => m.path === clean) ?? DEFAULT_META;
}
